import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "../services/firebase";

function sanitizeFileName(name = "photo.jpg") {
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : ".jpg";
  return `${base.replace(/[^a-zA-Z0-9_-]+/g, "_")}${ext.replace(/[^a-zA-Z0-9.]+/g, "")}`;
}

function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

function photoUrl(photo) {
  if (!photo) return "";
  if (typeof photo === "string") return photo;
  return photo.previewUrl || photo.url || photo.downloadURL || photo.base64 || "";
}

function photoName(photo, index) {
  if (!photo) return `Photo ${index + 1}`;
  if (typeof photo === "string") return `Photo ${index + 1}`;
  return photo.name || photo.fileName || `Photo ${index + 1}`;
}

function photoStoragePath(photo) {
  if (!photo || typeof photo === "string") return "";
  return photo.storagePath || photo.fullPath || "";
}

function readImageDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height, url });
    };
    img.onerror = () => resolve({ width: 0, height: 0, url });
    img.src = url;
  });
}

async function compressImage(file, maxWidth = 1800, quality = 0.82) {
  const { width, height, url } = await readImageDimensions(file);

  try {
    if (!width || !height) return file;

    const scale = Math.min(1, maxWidth / width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );

    if (!blob) return file;

    return new File([blob], sanitizeFileName(file.name.replace(/\.[^.]+$/, ".jpg")), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function PhotoUploadBase64({
  jobId,
  assignmentDocId,
  docketDocId,
  runKey,
  index,
  existingPhotos = [],
  onUpdate,
  mirrorAssignmentIds = [],
}) {
  const auth = getAuth();
  const targetAssignmentId = assignmentDocId || jobId;
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingKey, setDeletingKey] = useState("");
  const [photos, setPhotos] = useState(ensureArray(existingPhotos));


  const assignmentDocIds = useMemo(() => {
    const ids = [targetAssignmentId, ...ensureArray(mirrorAssignmentIds)]
      .filter(Boolean)
      .filter((id, idx, arr) => arr.indexOf(id) === idx);
    return ids;
  }, [targetAssignmentId, mirrorAssignmentIds]);

  useEffect(() => {
    setPhotos(ensureArray(existingPhotos));
  }, [existingPhotos]);

  useEffect(() => {
    return () => {
      if (selectedPreview) URL.revokeObjectURL(selectedPreview);
    };
  }, [selectedPreview]);

  const hasSelection = !!selectedFile;

  const photoItems = useMemo(() => ensureArray(photos), [photos]);

  function resetSelection() {
    if (selectedPreview) URL.revokeObjectURL(selectedPreview);
    setSelectedFile(null);
    setSelectedPreview("");
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (selectedPreview) URL.revokeObjectURL(selectedPreview);
    setSelectedFile(file);
    setSelectedPreview(URL.createObjectURL(file));
  }

  async function readAssignmentSnapshot() {
    const assignmentRef = doc(db, "assignments", targetAssignmentId);
    const snap = await getDoc(assignmentRef);
    if (!snap.exists()) throw new Error("Assignment not found");
    const data = snap.data() || {};
    if (!data.runs?.[runKey]?.[index]) {
      throw new Error("Invalid runKey or index");
    }
    return { assignmentRef, data };
  }




  async function updateAssignmentPhotos(nextPhotos) {
    const updateOne = async (docId) => {
      if (!docId) return;
      const assignmentRef = doc(db, "assignments", docId);
      const snap = await getDoc(assignmentRef);
      if (!snap.exists()) return;

      const data = snap.data() || {};
      const nextRuns = { ...(data.runs || {}) };
      const runList = Array.isArray(nextRuns?.[runKey]) ? [...nextRuns[runKey]] : [];
      if (!runList[index]) return;

      const row = { ...(runList[index] || {}) };
      row.proofPhotos = nextPhotos;
      row.proofPhoto = nextPhotos[0] ? photoUrl(nextPhotos[0]) : "";
      row.proofUploadedAt = nextPhotos[0]?.uploadedAt || row.proofUploadedAt || "";
      row.uploadedBy = auth.currentUser?.uid || row.uploadedBy || "";

      runList[index] = row;
      nextRuns[runKey] = runList;

      await updateDoc(assignmentRef, {
        runs: nextRuns,
        updatedAt: Date.now(),
      });
    };

    await Promise.all(assignmentDocIds.map(updateOne));
  }

  async function syncJobPhotos(nextPhotos) {
    if (!jobId) return;

    try {
      await setDoc(
        doc(db, "jobs", jobId),
        {
          proofPhotos: nextPhotos,
          proofPhoto: nextPhotos[0] ? photoUrl(nextPhotos[0]) : "",
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Failed to sync proof photos to job:", error);
    }
  }

  async function syncDocketPhotos(nextPhotos) {
    if (!docketDocId) return;

    try {
      await setDoc(
        doc(db, "weighbridgeDockets", docketDocId),
        {
          proofPhotos: nextPhotos,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error("Failed to sync proof photos to weighbridge docket:", error);
    }
  }

  async function handleUpload() {
    if (!selectedFile || !targetAssignmentId || runKey == null || index == null) return;

    setUploading(true);
    try {
      const compressed = await compressImage(selectedFile);
      const safeName = sanitizeFileName(selectedFile.name || `photo_${Date.now()}.jpg`);
      const uid = auth.currentUser?.uid || "unknown";
      const path = `proofPhotos/${uid}/${targetAssignmentId}/${String(runKey).replace(/\s+/g, "_")}/${index}/${Date.now()}_${safeName}`;
      const fileRef = storageRef(storage, path);

      await uploadBytes(fileRef, compressed, {
        contentType: compressed.type || "image/jpeg",
      });

      const url = await getDownloadURL(fileRef);
      const newPhoto = {
        name: safeName,
        url,
        storagePath: path,
        contentType: compressed.type || "image/jpeg",
        uploadedAt: new Date().toISOString(),
        uploadedBy: uid,
      };

      const { data } = await readAssignmentSnapshot();
      const runList = Array.isArray(data.runs?.[runKey]) ? [...data.runs[runKey]] : [];
      const row = { ...(runList[index] || {}) };
      const priorPhotos = ensureArray(row.proofPhotos);
      const nextPhotos = [...priorPhotos, newPhoto];

      await updateAssignmentPhotos(nextPhotos);

      await Promise.all([
        syncJobPhotos(nextPhotos),
        syncDocketPhotos(nextPhotos),
      ]);

      setPhotos(nextPhotos);
      resetSelection();
      onUpdate?.();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error?.message || "Failed to upload photo.");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(photo, photoIndex) {
    if (!targetAssignmentId || runKey == null || index == null) return;

    const key = `${photoStoragePath(photo) || photoUrl(photo) || photoIndex}`;
    setDeletingKey(key);

    try {
      const { data } = await readAssignmentSnapshot();
      const runList = Array.isArray(data.runs?.[runKey]) ? [...data.runs[runKey]] : [];
      const row = { ...(runList[index] || {}) };
      const priorPhotos = ensureArray(row.proofPhotos);
      const nextPhotos = priorPhotos.filter((_, i) => i !== photoIndex);

      const storagePath = photoStoragePath(photo);
      if (storagePath) {
        try {
          await deleteObject(storageRef(storage, storagePath));
        } catch (storageErr) {
          console.warn("Storage delete skipped/failed:", storageErr);
        }
      }

      await updateAssignmentPhotos(nextPhotos);

      await Promise.all([
        syncJobPhotos(nextPhotos),
        syncDocketPhotos(nextPhotos),
      ]);

      setPhotos(nextPhotos);
      onUpdate?.();
    } catch (error) {
      console.error("Delete error:", error);
      alert(error?.message || "Failed to delete photo.");
    } finally {
      setDeletingKey("");
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-xl font-semibold text-gray-900">Upload Photo</h3>

      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-xl file:border-0 file:bg-gray-100 file:px-4 file:py-3 file:text-sm file:font-medium file:text-gray-800 hover:file:bg-gray-200"
        />

        <button
          type="button"
          onClick={handleUpload}
          disabled={!hasSelection || uploading}
          className={`inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white transition ${
            !hasSelection || uploading
              ? "cursor-not-allowed bg-blue-300"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {uploading ? "Uploading..." : "Submit Photo"}
        </button>
      </div>

      {selectedFile && (
        <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900">
                {selectedFile.name}
              </div>
              <div className="text-xs text-gray-500">Ready to upload</div>
            </div>
            <button
              type="button"
              onClick={resetSelection}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>

          {selectedPreview && (
            <img
              src={selectedPreview}
              alt="Selected preview"
              className="max-h-64 w-full rounded-xl border border-blue-200 object-contain bg-white"
            />
          )}
        </div>
      )}

      <div className="mt-4">
        <div className="mb-2 text-sm font-medium text-gray-700">Uploaded photos</div>

        {photoItems.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
            No photos uploaded yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {photoItems.map((photo, photoIndex) => {
              const url = photoUrl(photo);
              const name = photoName(photo, photoIndex);
              const deleteKey = `${photoStoragePath(photo) || url || photoIndex}`;
              const deleting = deletingKey === deleteKey;

              return (
                <div
                  key={deleteKey}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50"
                >
                  <div className="aspect-[4/3] bg-white">
                    {url ? (
                      <img
                        src={url}
                        alt={name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-4 text-sm text-gray-500">
                        Preview unavailable
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 p-3">
                    <div className="truncate text-sm font-medium text-gray-900">{name}</div>

                    <div className="flex gap-2">
                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex flex-1 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100"
                        >
                          View
                        </a>
                      )}

                      <button
                        type="button"
                        onClick={() => handleDelete(photo, photoIndex)}
                        disabled={deleting}
                        className={`inline-flex flex-1 items-center justify-center rounded-lg px-3 py-2 text-xs font-medium text-white ${
                          deleting
                            ? "cursor-not-allowed bg-red-300"
                            : "bg-red-600 hover:bg-red-700"
                        }`}
                      >
                        {deleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
